import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import useIsMobile from '../../utils/isMobile';
import './index.scss';

interface MobileSearchBarProps {
  showTitle?: boolean;
  initialQuery?: string;
}

export const MobileSearchBar: FC<MobileSearchBarProps> = memo(({ showTitle = true, initialQuery = '' }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const params = useParams<{ search?: string }>();
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);

  const currentParam = params.search || initialQuery || '';
  const [query, setQuery] = useState<string>(currentParam);
  const [debouncedQuery] = useDebounce(query, 450);

  // Sync state if url param changes externally (e.g. browser back/forward or clear)
  // But NEVER overwrite query if user is actively typing in the input!
  useEffect(() => {
    if (!isFocusedRef.current) {
      setQuery(currentParam);
    }
  }, [currentParam]);

  // Handle navigation on debounced input change ONLY when the user is actively focused on this input
  useEffect(() => {
    if (!isFocusedRef.current) return;

    const trimmed = debouncedQuery.trim();
    const currentTrimmed = (params.search || initialQuery || '').trim();
    if (trimmed && trimmed !== currentTrimmed) {
      navigate(`/search/${encodeURIComponent(trimmed)}`, { replace: true });
    } else if (!trimmed && currentTrimmed) {
      navigate('/search', { replace: true });
    }
  }, [debouncedQuery]);

  // If user was actively focused, ensure input retains focus across route and state transitions
  useEffect(() => {
    if (isFocusedRef.current && document.activeElement !== inputRef.current) {
      inputRef.current?.focus({ preventScroll: true });
    }
  });

  if (!isMobile) return null;

  const handleClear = () => {
    setQuery('');
    navigate('/search', { replace: true });
    inputRef.current?.focus({ preventScroll: true });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed) {
      navigate(`/search/${encodeURIComponent(trimmed)}`, { replace: true });
    }
    // Only dismiss keyboard when user explicitly taps Enter / Search on their keyboard
    inputRef.current?.blur();
  };

  return (
    <div className='mobile-search-container'>
      <div className={`mobile-search-title-wrap ${showTitle ? 'visible' : 'hidden'}`}>
        <h1 className='mobile-search-page-title'>Search</h1>
      </div>
      <form
        onSubmit={handleSubmit}
        className='mobile-search-input-box'
      >
        <span className='mobile-search-icon' aria-hidden='true'>
          <svg role='img' height='20' width='20' viewBox='0 0 24 24' fill='#000000'>
            <path d='M10.533 1.27893C5.35215 1.27893 1.12598 5.41887 1.12598 10.5579C1.12598 15.697 5.35215 19.8369 10.533 19.8369C12.767 19.8369 14.8235 19.0671 16.4402 17.7794L20.7929 22.132C21.1834 22.5226 21.8166 22.5226 22.2071 22.132C22.5976 21.7415 22.5976 21.1083 22.2071 20.7178L17.8634 16.3741C19.1616 14.7849 19.94 12.7634 19.94 10.5579C19.94 5.41887 15.7138 1.27893 10.533 1.27893ZM3.12598 10.5579C3.12598 6.55226 6.42768 3.27893 10.533 3.27893C14.6383 3.27893 17.94 6.55226 17.94 10.5579C17.94 14.5636 14.6383 17.8369 10.533 17.8369C6.42768 17.8369 3.12598 14.5636 3.12598 10.5579Z' />
          </svg>
        </span>
        <input
          ref={inputRef}
          type='search'
          className='mobile-search-input'
          placeholder='What do you want to listen to?'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onBlur={() => {
            isFocusedRef.current = false;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit(e);
            }
          }}
          autoCapitalize='off'
          autoCorrect='off'
          spellCheck='false'
          aria-label='Search query'
        />
        {query ? (
          <button
            type='button'
            className='mobile-search-clear-btn'
            onClick={handleClear}
            aria-label='Clear search query'
          >
            <svg role='img' height='18' width='18' viewBox='0 0 24 24' fill='#535353'>
              <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
            </svg>
          </button>
        ) : null}
      </form>
    </div>
  );
});

MobileSearchBar.displayName = 'MobileSearchBar';
export default MobileSearchBar;
